import logger from '@/utilities/Logger'

const DEVICE_FILTER = [
	{ vendorId: 0x4d8, productId: 0xf848 }, // Bl Model 1
	{ vendorId: 0x27BB, productId: 0x3BCD }, // Omega Model
	{ vendorId: 0x27BB, productId: 0x3BCA }, // Alpha Model
	{ vendorId: 0x27BB, productId: 0x3BCF }, // Omega Model 2
	{ vendorId: 0x27BB, productId: 0x3BCE }, // Alpha Model 2
	{ vendorId: 0x27BB, productId: 0x3bcb } // UC Model
]

const outputReportId = 0x00

interface BusylightColor {
	/** 0-100 red */
	rgbred: number
	/** 0-100 blue */
	rgbblue: number
	/** 0-100 green */
	rgbgreen: number
}

class BusylightCommandStep {
	NextStep: number

	RepeatInterval: number

	Color: BusylightColor

	OnTimeSteps: number

	OffTimeSteps: number

	AudioByte: number

	constructor() {
		this.NextStep = 0
		this.RepeatInterval = 0
		this.Color = { rgbred: 0, rgbgreen: 0, rgbblue: 0 }
		this.OnTimeSteps = 0
		this.OffTimeSteps = 0
		this.AudioByte = 0
	}
}

export enum BusylightSoundclips {
	OpenOffice = 1,
	Quiet = 2,
	Funky = 3,
	FairyTale = 4,
	KuandoTrain = 5,
	TelephoneNordic = 6,
	TelephoneOriginal = 7,
	TelephonePickMeUp = 8,
	IM1 = 9,
	IM2 = 10
}

export enum BusylightVolume {
	Max = 6,
	High = 5,
	Middle = 3,
	Low = 2,
	Mute = 0
}

const BusylightPulseSeqence = { // eslint-disable-line
	Color: { rgbred: 100, rgbgreen: 100, rgbblue: 0 },
	Step1: 3,
	Step2: 21,
	Step3: 36,
	Step4: 50,
	Step5: 36,
	Step6: 21,
	Step7: 10
}

export default class KuandoWebHID {
	outputReport = new Uint8Array([64])

	busylightdevices: HIDDevice[] = []

	device?: HIDDevice = undefined

	constructor() {
		navigator.hid.addEventListener('connect', event => {
			logger.debug(`KuandoWebHID: Device connected: ${event.device.productName}`)
		})

		navigator.hid.addEventListener('disconnect', event => {
			logger.debug(`KuandoWebHID: Device disconnected: ${event.device.productName}`)
		})
	}

	handleInputReport(event: HIDInputReportEvent) {
		if (1 + 3 === 2) {
			logger.debug(`${event.device.productName}: got input report ${event.reportId}`)
			logger.debug(new Uint8Array(event.data.buffer).toString())
		}
	}

	async start(showPicker: boolean, onConnected?: () => void) {
		this.device = await this.getDevice(showPicker)
		if (!this.device) { return false }
		if (this.device.opened) { return true }

		// Open and reset the device.
		await this.device.open()
		logger.debug(`KuandoWebHID: Opened device: ${this.device.productName}`)
		this.device.addEventListener('inputreport', this.handleInputReport.bind(this))
		if (onConnected) { onConnected() }
		this.SetTimer()
		return true
	}

	async getDevice(showPicker: boolean) {
		const previousDevice = await this.getPreviousDevice()

		if (previousDevice) { return previousDevice }
		if (showPicker) {
			const opts = { filters: DEVICE_FILTER }
			const devices = await navigator.hid.requestDevice(opts)

			return devices[0]
		}
		return undefined
	}

	async getPreviousDevice() {
		const devices = await navigator.hid.getDevices()

		for (const device of devices) {
			if (device.vendorId === 10171) {
				this.device = device
				return device
			}
		}
		return undefined
	}

	GenerateCommands(steps: BusylightCommandStep[]) {
		const bytes = new Uint8Array(64)
		let counter = 0

		for (const step of steps) {
			if ((step.NextStep & 0xF0) == 0) { // eslint-disable-line no-bitwise
				bytes[counter] = step.NextStep | 0x10 // eslint-disable-line no-bitwise
			} else {
				bytes[counter] = step.NextStep
			}

			counter++
			bytes[counter] = step.RepeatInterval
			counter++

			if (step.Color) {
				bytes[counter] = step.Color.rgbred
				counter++
				bytes[counter] = step.Color.rgbgreen
				counter++
				bytes[counter] = step.Color.rgbblue
				counter++
			}

			bytes[counter] = step.OnTimeSteps
			counter++
			bytes[counter] = step.OffTimeSteps
			counter++
			bytes[counter] = step.AudioByte
			counter++
		}

		if (counter < bytes.length) {
			for (let index = counter; index < bytes.length; index++) {
				bytes[index] = 0
			}
		}

		for (let index = 59; index <= 61; index++) {
			bytes[index] = 0xff
		}

		let checksum = 0

		for (let index = 0; index < 62; index++) {
			checksum += bytes[index] as number
		}

		bytes[62] = (checksum / 256)
		bytes[63] = (checksum % 256)

		return bytes
	}

	GetAudioByte(soundclip: BusylightSoundclips, volume: BusylightVolume) {
		let retval = 0x80

		retval += (soundclip * 0x08) + volume
		return retval
	}

	async WriteToDevice(bytes: Uint8Array<ArrayBuffer>) {
		await this.device?.sendReport(outputReportId, bytes)
		// logger.debug(`KuandoWebHID: Sent output report ${outputReportId}`)
	}

	SetTimer() {
		setTimeout(() => { // Worker timeout overrides setTimeout. This is considered bad practice, so should probably be used as an utility instead
			const cmd = new BusylightCommandStep()

			cmd.NextStep = 0x8F
			const cmdbytes = this.GenerateCommands([cmd])

			this.WriteToDevice(cmdbytes)
			this.SetTimer() // Start new timer
		}, 10000)
	}

	GetPulseColor(intensity: number, color: BusylightColor) {
		const intens = Math.min(100, intensity)

		return { rgbred: (color.rgbred * intens) / 100, rgbgreen: (color.rgbgreen * intens) / 100, rgbblue: (color.rgbblue * intens) / 100 } as BusylightColor
	}

	ColorRGB(red: number, green: number, blue: number, soundclip?: BusylightSoundclips, volume?: BusylightVolume) {
		this.Color({ rgbred: red, rgbgreen: green, rgbblue: blue }, soundclip, volume)
	}

	Color(color: BusylightColor, soundclip?: BusylightSoundclips, volume?: BusylightVolume) {
		logger.debug(`KuandoWebHID: Setting color to ${color.rgbred}, ${color.rgbgreen}, ${color.rgbblue}`)
		const cmd = new BusylightCommandStep()

		cmd.Color = color
		cmd.AudioByte = soundclip && volume ? this.GetAudioByte(soundclip, volume) : 128
		cmd.NextStep = 0
		cmd.OffTimeSteps = 0
		cmd.OnTimeSteps = 1
		cmd.RepeatInterval = 1
		const cmdbytes = this.GenerateCommands([cmd])

		this.WriteToDevice(cmdbytes)
	}

	Alert(color: BusylightColor, soundclip: BusylightSoundclips, volume: BusylightVolume) {
		logger.debug(`KuandoWebHID: Alerting with color ${color.rgbred}, ${color.rgbgreen}, ${color.rgbblue}, soundclip ${soundclip}, volume ${volume}`)
		const cmd = new BusylightCommandStep()

		cmd.Color = color
		cmd.AudioByte = this.GetAudioByte(soundclip, volume)
		cmd.NextStep = 0
		cmd.OffTimeSteps = 5
		cmd.OnTimeSteps = 3
		cmd.RepeatInterval = 120
		const cmdbytes = this.GenerateCommands([cmd])

		this.WriteToDevice(cmdbytes)
	}

	Blink(color: BusylightColor, ontime: number, offtime: number, soundclip?: BusylightSoundclips, volume?: BusylightVolume) {
		logger.debug(`KuandoWebHID: Blinking with color ${color.rgbred}, ${color.rgbgreen}, ${color.rgbblue}, ontime ${ontime}, offtime ${offtime}`)
		const cmd = new BusylightCommandStep()

		cmd.Color = color
		cmd.AudioByte = soundclip && volume ? this.GetAudioByte(soundclip, volume) : 128
		cmd.NextStep = 0
		cmd.OffTimeSteps = offtime
		cmd.OnTimeSteps = ontime
		cmd.RepeatInterval = 0
		const cmdbytes = this.GenerateCommands([cmd])

		this.WriteToDevice(cmdbytes)
	}

	Sequence(sequence: { color: BusylightColor, duration: number }[], _soundclip?: BusylightSoundclips, _volume?: BusylightVolume) {
		logger.debug(`KuandoWebHID: Pulsing with color ${sequence.length} steps`)

		const commands: BusylightCommandStep[] = []
		let index = 0

		for (const seq of sequence) {
			index++
			const cmd = new BusylightCommandStep()

			cmd.Color = seq.color
			cmd.AudioByte = 128
			cmd.NextStep = index < sequence.length ? index : 0
			cmd.OffTimeSteps = 0
			cmd.OnTimeSteps = seq.duration
			cmd.RepeatInterval = 0

			commands.push(cmd)
		}

		const cmdbytes = this.GenerateCommands(commands)

		this.WriteToDevice(cmdbytes)
	}

	Pulse(color: BusylightColor, percentPrStep: number, speed: number, _soundclip?: BusylightSoundclips, _volume?: BusylightVolume) {
		logger.debug(`KuandoWebHID: Pulsing with color ${color.rgbred}, ${color.rgbgreen}, ${color.rgbblue}, percentPrStep ${percentPrStep}, speed ${speed}`)
		const commands: BusylightCommandStep[] = []
		const intensities: number[] = [
			100,
			100 - percentPrStep,
			100 - (percentPrStep * 2),
			100 - (percentPrStep * 3),
			100 - (percentPrStep * 2),
			100 - percentPrStep
		]
		let index = 0

		for (const intensity of intensities) {
			index++
			const cmd = new BusylightCommandStep()

			cmd.Color = this.GetPulseColor(intensity, color)
			cmd.AudioByte = 128
			cmd.NextStep = index < 6 ? index : 0
			cmd.OffTimeSteps = 0
			cmd.OnTimeSteps = speed
			cmd.RepeatInterval = 0

			commands.push(cmd)
		}

		this.WriteToDevice(this.GenerateCommands(commands))
	}

	ColorWithFlash(color: BusylightColor, flashcolor: BusylightColor, _soundclip?: BusylightSoundclips, _volume?: BusylightVolume) {
		logger.debug(`KuandoWebHID: Flashing with color ${color.rgbred}, ${color.rgbgreen}, ${color.rgbblue}, flashcolor ${flashcolor.rgbred}, ${flashcolor.rgbgreen}, ${flashcolor.rgbblue}`)
		const cmd1 = new BusylightCommandStep()

		cmd1.Color = flashcolor
		cmd1.AudioByte = 128
		cmd1.NextStep = 1
		cmd1.OffTimeSteps = 0
		cmd1.OnTimeSteps = 5
		cmd1.RepeatInterval = 1

		const cmd2 = new BusylightCommandStep()

		cmd2.Color = color
		cmd2.AudioByte = 128
		cmd2.NextStep = 0
		cmd2.OffTimeSteps = 0
		cmd2.OnTimeSteps = 30
		cmd2.RepeatInterval = 0
		const cmdbytes = this.GenerateCommands([cmd1, cmd2])

		this.WriteToDevice(cmdbytes)
	}
}
